import axios from 'axios';
import React, { useState, useContext, useEffect } from 'react';
import { MdDeleteForever } from 'react-icons/md';
import { AuthContext } from '../../AuthContext';
import UseToast from '../UseToast';

const multipleApiInputsArray = [
  {
    label: 'Heygen Api Keys',
    name: 'heygen_api_keys',
    placeholder: 'Enter Heygen API Key',
  },
  {
    label: 'Avatar Key Ids',
    name: 'avatar_api_keys',
    placeholder: 'Enter Avatar Key Id',
  },
  {
    label: 'Voice Key Ids',
    name: 'voice_api_keys',
    placeholder: 'Enter Voice Key Id',
  },
  {
    label: 'Openai Api Keys',
    name: 'openai_api_keys',
    placeholder: 'Enter Openai API Key',
  },
];

export const AddApiKeysAdmin = () => {
  const [formData, setFormData] = useState({
    heygen_api_keys: [''],
    avatar_api_keys: [''],
    voice_api_keys: [''],
    openai_api_keys: [''],
  });
  const [initialFormData, setInitialFormData] = useState(null); // ✅ Track initial data

  const { showMsgSuccessToast, showErrorToast } = UseToast();
  const { authState } = useContext(AuthContext);
  const token = authState?.token;

  const [docExists, setDocExists] = useState(false);

  // ✅ Check for duplicates inside each API key array
  const hasDuplicates = data => {
    const fields = ['heygen_api_keys', 'avatar_api_keys', 'voice_api_keys', 'openai_api_keys'];

    for (const field of fields) {
      const arr = (data[field] || []).map(k => k.trim()).filter(k => k !== '');
      const unique = new Set(arr);
      if (unique.size !== arr.length) {
        return field; // return the field name where duplicates exist
      }
    }
    return null;
  };

  const cleanFormData = data => {
    const cleanedData = { ...data };
    const fields = ['heygen_api_keys', 'avatar_api_keys', 'voice_api_keys', 'openai_api_keys'];
    fields.forEach(field => {
      // Filter out blanks
      const filtered = (data[field] || []).filter(key => key.trim() !== '');
      // ✅ Always ensure at least one blank slot
      cleanedData[field] = filtered.length > 0 ? filtered : [''];
    });
    return cleanedData;
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const updated = [...prev[field]];
      updated[index] = value;
      return { ...prev, [field]: updated };
    });
  };

  const handleAddField = field => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const handleRemoveField = (field, index) => {
    setFormData(prev => {
      const updated = [...prev[field]];
      if (updated.length === 1) {
        updated[0] = '';
      } else {
        updated.splice(index, 1);
      }
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const cleanedData = cleanFormData(formData);

    // 🔍 Duplicate validation
    const duplicateField = hasDuplicates(cleanedData);
    if (duplicateField) {
      showErrorToast(`Duplicate keys found in ${duplicateField.replace(/_/g, ' ')}`);
      return; // stop submission
    }

    try {
      let res;
      if (!docExists) {
        res = await axios.post('/api/api-keys/add-api-keys', cleanedData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDocExists(true);
      } else {
        res = await axios.put('/api/api-keys', cleanedData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (res.status === 200 || res.status === 201) {
        const apiKeys = res.data.apiKeys || {};
        const safeData = {
          heygen_api_keys: apiKeys.heygen_api_keys || [''],
          avatar_api_keys: apiKeys.avatar_api_keys || [''],
          voice_api_keys: apiKeys.voice_api_keys || [''],
          openai_api_keys: apiKeys.openai_api_keys || [''],
        };

        setFormData(safeData);
        setInitialFormData(safeData);
        setDocExists(true);
        showMsgSuccessToast(res.data.message);
      }
    } catch (err) {
      showErrorToast(err?.response?.data || 'Failed to save API keys');
      console.error(err);
    }
  };

  // ✅ Check if form changed
  const isFormChanged =
    JSON.stringify(cleanFormData(formData)) !==
    JSON.stringify(cleanFormData(initialFormData || {}));

  // ✅ Check if any non-empty key exists
  const hasValidInput = Object.values(cleanFormData(formData)).some(arr => arr.length > 0);

  const isButtonDisabled = !isFormChanged || !hasValidInput;

  useEffect(() => {
    const fetchKeys = async () => {
      try {
        const res = await axios.get('/api/api-keys', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 200 && res.data) {
          const normalized = cleanFormData(res.data);
          setFormData(normalized);
          setInitialFormData(normalized); // ✅ store original copy
          setDocExists(true);
        } else {
          setDocExists(false);
        }
      } catch (err) {
        console.error(err);
        setDocExists(false);
      }
    };
    fetchKeys();
  }, [token]);

  return (
    <div className="card fade-in">
      <div className="card-header">
        <h5 className="card-title">
          <i className="fas fa-building me-2"></i> Manage API Keys
        </h5>
        <p className="card-text mb-0">
          Add, update, or remove Heygen, Avatar, Voice, and OpenAI API keys.
        </p>
      </div>
      <div className="card-body">
        <form style={{ padding: '20px 20px' }} onSubmit={handleSubmit}>
          <div className="weight-500 profile-edit-list row">
            {multipleApiInputsArray.map(field => (
              <div key={field.name} className="form-group col-md-6">
                <label className="font-weight-bold">{field.label}</label>
                {formData[field.name]?.map((key, idx) => (
                  <div key={idx} className="d-flex mb-2 align-items-center">
                    <input
                      type="text"
                      value={key}
                      onChange={e => handleArrayChange(field.name, idx, e.target.value)}
                      placeholder={field.placeholder}
                      className="form-control mr-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field.name, idx)}
                      className="btn btn-sm"
                    >
                      <MdDeleteForever style={{ fontSize: '25px' }} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddField(field.name)}
                  className="btn btn-link"
                >
                  + Add More
                </button>
              </div>
            ))}
          </div>

          {/* ✅ Save vs Update button */}
          <div style={{ textAlign: 'end' }}>
            <button
              type="submit"
              className="btn btn-enhanced btn-primary-enhanced px-5"
              disabled={isButtonDisabled}
            >
              {docExists ? 'Update Keys' : 'Save Keys'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
